import { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import api from '@/lib/api';

const SiteDataContext = createContext(null);

export const SiteDataProvider = ({ children }) => {
  const [data, setData] = useState({
    leaders: null, departments: null, churches: null,
    gallery: null, timeline: null, settings: null,
    nav: null, pageContent: {},
  });
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async (keys) => {
    const endpoints = {
      leaders: '/site/leaders', departments: '/site/departments',
      churches: '/site/churches', gallery: '/site/gallery',
      timeline: '/site/timeline', settings: '/site/settings',
      nav: '/site/nav',
    };
    const targets = keys || Object.keys(endpoints);
    const results = await Promise.all(targets.map(k =>
      api.get(endpoints[k]).then(r => [k, r.data]).catch(() => [k, null])
    ));
    setData(prev => {
      const next = { ...prev };
      results.forEach(([k, v]) => { if (v !== null) next[k] = v; });
      return next;
    });
    setLoading(false);
  }, []);

  const loadPageContent = useCallback(async (slug) => {
    try {
      const { data: pc } = await api.get(`/site/page-content/${slug}`);
      setData(prev => ({ ...prev, pageContent: { ...prev.pageContent, [slug]: pc } }));
      return pc;
    } catch { return null; }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Refresh whenever the tab regains focus so any admin edits made in another
  // window (or on the deployed site) reflect immediately on return.
  useEffect(() => {
    const onFocus = () => refresh();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [refresh]);

  const value = useMemo(
    () => ({ ...data, loading, refresh, loadPageContent }),
    [data, loading, refresh, loadPageContent]
  );

  return (
    <SiteDataContext.Provider value={value}>
      {children}
    </SiteDataContext.Provider>
  );
};

export const useSiteData = () => useContext(SiteDataContext);

export function usePageContent(slug) {
  const ctx = useContext(SiteDataContext);
  const [c, setC] = useState({});
  useEffect(() => {
    if (!ctx || !slug) return;
    if (ctx.pageContent?.[slug]) { setC(ctx.pageContent[slug]); return; }
    ctx.loadPageContent?.(slug).then(pc => setC(pc || {}));
  }, [ctx, slug, ctx?.pageContent?.[slug]]);
  return c;
}
